import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { makeApiCall, makeJsonApiCall } from '../utils/customerUtils'
import { AlertTriangle, X, Edit, Trash2, Eye } from 'lucide-react'

interface Task {
  id: number
  description: string
  due_date: string | null
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled'
  priority: 'low' | 'medium' | 'high'
  task_type: string
  follow_up_type: string | null
  notes: string | null
  created_at: string
  updated_at: string
  completed_at: string | null
  contextName: string
  contextType: 'customer' | 'lead' | 'deal' | 'general'
  userName: string
  user_email: string
  isOverdue: boolean
  customer_id: number | null
  lead_id: number | null
  deal_id: number | null
}

interface TaskDetails extends Task {
  contextDetails: any
  createdByName: string
  updatedByName: string | null
  completedByName: string | null
  relatedTasks: Array<{
    id: number
    description: string
    due_date: string | null
    status: string
    task_type: string
    created_at: string
    parent_task_id: number | null
  }>
  childTasks: Array<{
    id: number
    description: string
    due_date: string | null
    status: string
    task_type: string
    created_at: string
  }>
  parentTask: {
    id: number
    description: string
    completed_at: string | null
  } | null
}

interface User {
  id: number
  first_name: string
  last_name: string
  email: string
  role: string
}

const AdminTasks: React.FC = () => {
  const { t } = useTranslation()
  const [tasks, setTasks] = useState<Task[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // Filters and pagination
  const [selectedUser, setSelectedUser] = useState<string>('')
  const [selectedStatus, setSelectedStatus] = useState<string>('')
  const [currentPage, setCurrentPage] = useState(0)
  const [totalTasks, setTotalTasks] = useState(0)
  const limit = 20

  // Modals
  const [showTaskModal, setShowTaskModal] = useState(false)
  const [selectedTask, setSelectedTask] = useState<TaskDetails | null>(null)
  const [modalLoading, setModalLoading] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingTask, setEditingTask] = useState<Task | null>(null)

  // Edit form
  const [editForm, setEditForm] = useState({
    description: '',
    due_date: '',
    status: '',
    priority: '',
    notes: '',
    customer_id: null as number | null,
    deal_id: null as number | null,
    lead_id: null as number | null
  })

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    in_progress: 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800'
  }

  const priorityColors = {
    low: 'bg-gray-100 text-gray-800',
    medium: 'bg-orange-100 text-orange-800',
    high: 'bg-red-100 text-red-800'
  }

  const contextTypeIcons = {
    customer: '👤',
    lead: '🎯', 
    deal: '💼',
    general: '📋'
  }

  useEffect(() => {
    fetchTasks()
    fetchUsers()
  }, [selectedUser, selectedStatus, currentPage])

  const fetchTasks = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: (currentPage * limit).toString()
      })
      
      if (selectedUser) params.append('user_id', selectedUser)
      if (selectedStatus) params.append('status', selectedStatus)

      const response = await makeApiCall(`/api/admin/tasks?${params}`, {
        method: 'GET'
      })
      
      if (!response.ok) {
        throw new Error('Failed to fetch tasks')
      }
      
      const data = await response.json()
      setTasks(data.tasks || [])
      setTotalTasks(data.pagination?.total || 0)
    } catch (err: any) {
      console.error('Error fetching tasks:', err)
      setError(err.message || 'Failed to fetch tasks')
    } finally {
      setLoading(false)
    }
  }

  const fetchUsers = async () => {
    try {
      const response = await makeApiCall('/api/admin/users', {
        method: 'GET'
      })
      
      if (!response.ok) {
        throw new Error('Failed to fetch users')
      }
      
      const data = await response.json()
      setUsers(data.users || [])
    } catch (err: any) {
      console.error('Error fetching users:', err)
    }
  }

  const fetchTaskDetails = async (taskId: number) => {
    try {
      setModalLoading(true)
      const response = await makeApiCall(`/api/admin/tasks/${taskId}`, {
        method: 'GET'
      })
      
      if (!response.ok) {
        throw new Error('Failed to fetch task details')
      }
      
      const data = await response.json()
      setSelectedTask(data.task)
    } catch (err: any) {
      console.error('Error fetching task details:', err)
      setError(err.message || 'Failed to fetch task details')
    } finally {
      setModalLoading(false)
    }
  }

  const handleViewTask = (task: Task) => {
    setShowTaskModal(true)
    fetchTaskDetails(task.id)
  }

  const handleEditTask = (task: Task) => {
    setEditingTask(task)
    setEditForm({
      description: task.description,
      due_date: task.due_date ? task.due_date.split('T')[0] : '',
      status: task.status,
      priority: task.priority,
      notes: task.notes || '',
      // CRITICAL: Preserve relationships to prevent accidental clearing
      customer_id: task.customer_id,
      deal_id: task.deal_id,
      lead_id: task.lead_id
    })
    setShowEditModal(true)
  }

  const handleUpdateTask = async () => {
    if (!editingTask) return

    try {
      setModalLoading(true)
      const response = await makeJsonApiCall(`/api/admin/tasks/${editingTask.id}`, 'PUT', editForm)
      
      if (!response.ok) {
        throw new Error('Failed to update task')
      }
      
      setShowEditModal(false)
      setEditingTask(null)
      fetchTasks()
    } catch (err: any) {
      console.error('Error updating task:', err)
      setError(err.message || 'Failed to update task')
    } finally {
      setModalLoading(false)
    }
  }

  const handleDeleteTask = async (taskId: number) => {
    if (!confirm('Are you sure you want to delete this task? This action cannot be undone.')) {
      return
    }

    try {
      const response = await makeApiCall(`/api/admin/tasks/${taskId}`, {
        method: 'DELETE'
      })
      
      if (!response.ok) {
        throw new Error('Failed to delete task')
      }
      
      fetchTasks()
    } catch (err: any) {
      console.error('Error deleting task:', err)
      setError(err.message || 'Failed to delete task')
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'No due date'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const totalPages = Math.ceil(totalTasks / limit)

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Task Management</h1>
          <p className="text-gray-600">View and manage all tasks across the system</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-48">
              <label className="block text-sm font-medium text-gray-700 mb-1">Filter by User</label>
              <select
                value={selectedUser}
                onChange={(e) => {
                  setSelectedUser(e.target.value)
                  setCurrentPage(0)
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
              >
                <option value="">All Users</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.first_name} {user.last_name} ({user.email})
                  </option>
                ))}
              </select>
            </div>

            <div className="flex-1 min-w-48">
              <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Status</label>
              <select
                value={selectedStatus}
                onChange={(e) => {
                  setSelectedStatus(e.target.value)
                  setCurrentPage(0)
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={() => {
                  setSelectedUser('')
                  setSelectedStatus('')
                  setCurrentPage(0)
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-900"
              >
                Clear Filters
              </button>
            </div>
          </div>

          <div className="mt-3 text-sm text-gray-600">
            Showing {tasks.length} of {totalTasks} tasks
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex">
              <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-red-400 hover:text-red-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Tasks Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading tasks...</p>
            </div>
          ) : tasks.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              No tasks found with the current filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Task</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Context</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {tasks.map((task) => (
                    <tr key={task.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4">
                        <div>
                          <p className="text-sm font-medium text-gray-900 truncate max-w-xs" title={task.description}>
                            {task.description}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            {task.task_type === 'follow_up' && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                Follow-up
                              </span>
                            )}
                            {task.isOverdue && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                Overdue
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center">
                          <span className="text-lg mr-2">{contextTypeIcons[task.contextType]}</span>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{task.contextName}</p>
                            <p className="text-xs text-gray-500 capitalize">{task.contextType}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{task.userName}</p>
                          <p className="text-xs text-gray-500">{task.user_email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className={`text-sm ${task.isOverdue ? 'text-red-600 font-medium' : 'text-gray-900'}`}>
                          {formatDate(task.due_date)}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[task.status]}`}>
                          {task.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${priorityColors[task.priority]}`}>
                          {task.priority}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleViewTask(task)}
                            className="text-gray-900 hover:text-blue-800"
                            title="View details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEditTask(task)}
                            className="text-green-600 hover:text-green-800"
                            title="Edit task"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteTask(task.id)}
                            className="text-red-600 hover:text-red-800"
                            title="Delete task"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Page {currentPage + 1} of {totalPages}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0}
                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                disabled={currentPage >= totalPages - 1}
                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Task Details Modal */}
        {showTaskModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-900">Task Details</h2>
                  <button
                    onClick={() => setShowTaskModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {modalLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                    <p className="mt-2 text-gray-600">Loading task details...</p>
                  </div>
                ) : selectedTask && (
                  <div className="space-y-6">
                    {/* Basic Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-3">Task Information</h3>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Description</label>
                            <p className="mt-1 text-sm text-gray-900">{selectedTask.description}</p>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Due Date</label>
                            <p className="mt-1 text-sm text-gray-900">{formatDate(selectedTask.due_date)}</p>
                          </div>
                          <div className="flex gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Status</label>
                              <span className={`mt-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[selectedTask.status]}`}>
                                {selectedTask.status.replace('_', ' ')}
                              </span>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Priority</label>
                              <span className={`mt-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${priorityColors[selectedTask.priority]}`}>
                                {selectedTask.priority}
                              </span>
                            </div>
                          </div>
                          {selectedTask.notes && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Notes</label>
                              <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{selectedTask.notes}</p>
                            </div>
                          )}
                        </div>
                      </div>

                      <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-3">Context & Tracking</h3>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Associated With</label>
                            <div className="mt-1 flex items-center">
                              <span className="text-lg mr-2">{contextTypeIcons[selectedTask.contextType]}</span>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{selectedTask.contextName}</p>
                                <p className="text-xs text-gray-500 capitalize">{selectedTask.contextType}</p>
                              </div>
                            </div>
                            {selectedTask.contextDetails && (
                              <div className="mt-2 text-xs text-gray-600">
                                {selectedTask.contextDetails.email && <p>📧 {selectedTask.contextDetails.email}</p>}
                                {selectedTask.contextDetails.phone && <p>📞 {selectedTask.contextDetails.phone}</p>}
                                {selectedTask.contextDetails.amount && <p>💰 ${selectedTask.contextDetails.amount?.toLocaleString()}</p>}
                              </div>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Created by</label>
                            <p className="mt-1 text-sm text-gray-900">{selectedTask.createdByName}</p>
                            <p className="text-xs text-gray-500">{formatDateTime(selectedTask.created_at)}</p>
                          </div>
                          {selectedTask.updatedByName && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Last updated by</label>
                              <p className="mt-1 text-sm text-gray-900">{selectedTask.updatedByName}</p>
                              <p className="text-xs text-gray-500">{formatDateTime(selectedTask.updated_at)}</p>
                            </div>
                          )}
                          {selectedTask.completedByName && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700">Completed by</label>
                              <p className="mt-1 text-sm text-gray-900">{selectedTask.completedByName}</p>
                              <p className="text-xs text-gray-500">{formatDateTime(selectedTask.completed_at)}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Parent Task */}
                    {selectedTask.parentTask && (
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-3">Parent Task</h3>
                        <div className="bg-blue-50 rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">📋 {selectedTask.parentTask.description}</p>
                              <p className="text-xs text-gray-500 mt-1">
                                This task is a follow-up to the above task
                                {selectedTask.parentTask.completed_at && (
                                  <span className="ml-2">
                                    (completed {formatDateTime(selectedTask.parentTask.completed_at)})
                                  </span>
                                )}
                              </p>
                            </div>
                            <button
                              onClick={() => {
                                setShowTaskModal(false)
                                fetchTaskDetails(selectedTask.parentTask!.id)
                                setShowTaskModal(true)
                              }}
                              className="text-gray-900 hover:text-blue-800 text-sm font-medium"
                            >
                              View Parent
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Child Tasks */}
                    {selectedTask.childTasks.length > 0 && (
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-3">Follow-up Tasks</h3>
                        <div className="bg-green-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-3">Tasks created as follow-ups to this task:</p>
                          <div className="space-y-2">
                            {selectedTask.childTasks.map((childTask) => (
                              <div key={childTask.id} className="flex items-center justify-between p-2 bg-white rounded border">
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-gray-900">{childTask.description}</p>
                                  <div className="flex items-center gap-2 mt-1">
                                    <span className="text-xs text-gray-500">{formatDate(childTask.due_date)}</span>
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusColors[childTask.status as keyof typeof statusColors]}`}>
                                      {childTask.status}
                                    </span>
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                      Follow-up
                                    </span>
                                  </div>
                                </div>
                                <button
                                  onClick={() => {
                                    setShowTaskModal(false)
                                    fetchTaskDetails(childTask.id)
                                    setShowTaskModal(true)
                                  }}
                                  className="text-gray-900 hover:text-blue-800 text-sm"
                                >
                                  View
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Related Tasks */}
                    {selectedTask.relatedTasks.length > 0 && (
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-3">Related Tasks</h3>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-3">Other tasks for the same customer/lead/deal:</p>
                          <div className="space-y-2">
                            {selectedTask.relatedTasks.map((relatedTask) => (
                              <div key={relatedTask.id} className="flex items-center justify-between p-2 bg-white rounded border">
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-gray-900">{relatedTask.description}</p>
                                  <div className="flex items-center gap-2 mt-1">
                                    <span className="text-xs text-gray-500">{formatDate(relatedTask.due_date)}</span>
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusColors[relatedTask.status as keyof typeof statusColors]}`}>
                                      {relatedTask.status}
                                    </span>
                                    {relatedTask.task_type === 'follow_up' && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                        Follow-up
                                      </span>
                                    )}
                                    {relatedTask.parent_task_id && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                        Has Parent
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <button
                                  onClick={() => {
                                    setShowTaskModal(false)
                                    fetchTaskDetails(relatedTask.id)
                                    setShowTaskModal(true)
                                  }}
                                  className="text-gray-900 hover:text-blue-800 text-sm"
                                >
                                  View
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Edit Task Modal */}
        {showEditModal && editingTask && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-900">Edit Task</h2>
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      value={editForm.description}
                      onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                      <input
                        type="date"
                        value={editForm.due_date}
                        onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                      <select
                        value={editForm.status}
                        onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                      >
                        <option value="pending">Pending</option>
                        <option value="in_progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                    <select
                      value={editForm.priority}
                      onChange={(e) => setEditForm({ ...editForm, priority: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea
                      value={editForm.notes}
                      onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                      placeholder="Optional notes about this task..."
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-900"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateTask}
                    disabled={modalLoading}
                    className="px-4 py-2 text-sm font-medium text-white bg-gray-900 border border-transparent rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 disabled:opacity-50"
                  >
                    {modalLoading ? 'Updating...' : 'Update Task'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminTasks
